'use client';

import Link from 'next/link';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  title: string;
  icon: string;
  questions: FAQItem[];
}

export default function FAQPage() {
  const faqCategories: FAQCategory[] = [
    {
      id: 'ordering',
      title: 'Ordering Process',
      icon: 'ShoppingCartIcon',
      questions: [
        {
          id: 'how-to-order',
          question: 'How do I place an order?',
          answer: 'It\'s easy! Browse our products, select what you need, and submit a quote request through our contact form. Our sales team will respond within 24 hours with a detailed quote, payment terms, and delivery timeline. Registered customers can place orders directly through their account portal.'
        },
        {
          id: 'order-confirmation',
          question: 'How will I receive my order confirmation?',
          answer: 'You\'ll get an immediate email confirmation with your order number, itemized list, total amount, and estimated delivery date. Track your order status in real-time through your customer portal or contact our support team anytime.'
        },
        {
          id: 'modify-order',
          question: 'Can I modify or cancel my order?',
          answer: 'Yes! You can modify or cancel orders within 24 hours at no charge. After that, changes may have a 10% restocking fee if items are already being prepared. Contact our customer service team right away for any changes.'
        },
        {
          id: 'bulk-ordering',
          question: 'Do you help with large bulk orders?',
          answer: 'Absolutely! For bulk orders over €10,000, you get a dedicated account manager who handles everything from quote to delivery. We provide custom pricing, flexible payment terms, and priority logistics for high-volume purchases.'
        }
      ]
    },
    {
      id: 'payment',
      title: 'Payment Terms & Methods',
      icon: 'CreditCardIcon',
      questions: [
        {
          id: 'payment-methods',
          question: 'What payment methods do you accept?',
          answer: 'We accept bank transfers (SEPA/SWIFT), credit/debit cards (Visa, Mastercard, Amex), PayPal for orders under €5,000, and trade credit for established customers. All transactions are secured with SSL encryption and PCI-DSS compliance.'
        },
        {
          id: 'payment-terms',
          question: 'What are your payment terms?',
          answer: 'Standard terms are Net 30 for verified business customers. New customers may need 50% upfront with balance due before shipping. Established customers with good credit can qualify for Net 60 or Net 90 terms. Get 2% off for payments within 10 days.'
        },
        {
          id: 'credit-account',
          question: 'How do I apply for trade credit?',
          answer: 'Submit your business registration, VAT number, bank references, and trade references through our credit application form. Approval takes 3-5 business days. Credit limits range from €5,000 to €100,000 based on your business profile.'
        },
        {
          id: 'invoicing',
          question: 'When will I get my invoice?',
          answer: 'Invoices are sent immediately when your order is confirmed. You\'ll receive it via email in PDF format with detailed line items, VAT breakdown, payment terms, and bank details. Credit account holders get monthly statements through the customer portal.'
        }
      ]
    },
    {
      id: 'shipping',
      title: 'Shipping & Delivery',
      icon: 'TruckIcon',
      questions: [
        {
          id: 'shipping-time',
          question: 'How long does delivery take?',
          answer: 'Belgium & Netherlands: 1-2 business days. France, Germany, Luxembourg: 2-3 business days. Other EU countries: 3-5 business days. Express delivery available for urgent orders with next-day delivery in Belgium and 2-day across major EU cities.'
        },
        {
          id: 'shipping-costs',
          question: 'What are the shipping costs?',
          answer: 'Costs depend on order weight, volume, and destination. FREE SHIPPING on orders over €500 in Belgium and €1,000 for EU destinations. Standard rates: €15-€75 depending on location. Exact costs are in your quote before you order.'
        },
        {
          id: 'tracking',
          question: 'Can I track my shipment?',
          answer: 'Yes! All orders include real-time tracking. Once shipped, you\'ll get a tracking number via email with a link to monitor delivery status. Track shipments through your customer portal too. SMS notifications available on request.'
        },
        {
          id: 'delivery-issues',
          question: 'What if there\'s a delivery problem?',
          answer: 'Contact us immediately at +32 4 123 4567 or info@suppliesdistributorssa.com for any issues (damaged goods, missing items, delays). We offer full insurance on all shipments and arrange replacement or refund within 48 hours. Our 99.5% on-time delivery rate speaks for itself.'
        }
      ]
    },
    {
      id: 'minimum-orders',
      title: 'Minimum Order Quantities',
      icon: 'CalculatorIcon',
      questions: [
        {
          id: 'moq-general',
          question: 'Is there a minimum order quantity?',
          answer: 'Minimum order value is €250 for first-time customers and €150 for established customers. This ensures competitive wholesale pricing while maintaining quality service. Individual product MOQs vary: typically 10-50 units for office supplies, 5-20 for cleaning products, and case quantities for food items.'
        },
        {
          id: 'sample-orders',
          question: 'Can I order samples first?',
          answer: 'Yes! We encourage sample orders to ensure satisfaction. Order up to 5 items with a €50 minimum. Sample costs are fully credited toward your first bulk order over €1,000 placed within 60 days. Verify quality before committing to larger quantities.'
        },
        {
          id: 'mixed-orders',
          question: 'Can I mix different products in one order?',
          answer: 'Absolutely! Mix products across all categories in a single order as long as you meet the minimum value. This helps you consolidate shipments, reduce freight costs, and simplify inventory. Our system automatically calculates the best packaging for mixed orders.'
        }
      ]
    },
    {
      id: 'quality',
      title: 'Product Quality Standards',
      icon: 'StarIcon',
      questions: [
        {
          id: 'quality-assurance',
          question: 'How do you ensure product quality?',
          answer: 'Quality is our priority. All products undergo rigorous inspection before shipping. We source exclusively from ISO-certified manufacturers, conduct random quality audits, maintain climate-controlled warehouses, and implement strict quality protocols. Every shipment includes a quality certificate and batch traceability.'
        },
        {
          id: 'product-warranty',
          question: 'Do products come with a warranty?',
          answer: 'Yes! All products include a minimum 12-month manufacturer warranty covering defects in materials and workmanship. Extended warranties up to 36 months available for premium lines. Warranty claims processed within 5 business days with free replacement shipping.'
        },
        {
          id: 'returns-policy',
          question: 'What\'s your returns policy?',
          answer: 'We offer a 30-day satisfaction guarantee. Not completely satisfied? Return unused products in original packaging for a full refund or exchange. Defective items replaced at no cost with free return shipping. Returns processed within 3-5 business days.'
        },
        {
          id: 'product-authenticity',
          question: 'Are all products authentic?',
          answer: 'Guaranteed! We\'re authorized distributors for all brands we carry. Every product is 100% authentic, sourced directly from manufacturers or authorized wholesalers. We provide certificates of authenticity, batch numbers, and manufacturer documentation on request. No counterfeits ever.'
        }
      ]
    },
    {
      id: 'certifications',
      title: 'Certifications & Compliance',
      icon: 'ShieldCheckIcon',
      questions: [
        {
          id: 'business-certifications',
          question: 'What certifications does your company hold?',
          answer: 'SDSA holds multiple industry certifications: ISO 9001:2015 (Quality Management), ISO 14001:2015 (Environmental Management), OHSAS 18001 (Occupational Health & Safety), HACCP Certified for food-grade products, and GDP Certified for pharmaceutical supplies. All audited annually.'
        },
        {
          id: 'product-compliance',
          question: 'Are products compliant with EU regulations?',
          answer: 'Yes! All products meet or exceed EU safety and quality standards including CE marking, REACH compliance, RoHS directive compliance, and EU food safety regulations where applicable. We maintain comprehensive compliance documentation for all products.'
        },
        {
          id: 'safety-documentation',
          question: 'Can I get safety data sheets and compliance documents?',
          answer: 'Yes! Safety Data Sheets (SDS), compliance certificates, test reports, and product specifications are available for all products. Download them from product pages or request from our customer service team. Available in English, French, Dutch, and German.'
        },
        {
          id: 'environmental-commitment',
          question: 'What\'s your environmental commitment?',
          answer: 'Sustainability is core to our operations. We prioritize eco-friendly products, use recyclable packaging, operate carbon-neutral logistics where possible, partner with environmentally responsible manufacturers, and maintain waste reduction programs. Over 40% of our products carry eco-certifications.'
        }
      ]
    },
    {
      id: 'bulk-discounts',
      title: 'Bulk Discounts & Pricing',
      icon: 'ReceiptPercentIcon',
      questions: [
        {
          id: 'volume-discounts',
          question: 'Do you offer volume discounts?',
          answer: 'Yes! Tiered discounts: 5% off €2,500-€5,000, 10% off €5,000-€10,000, 15% off €10,000-€25,000, and 20%+ off orders over €25,000. Additional discounts for annual contracts and recurring orders. Contact sales for custom pricing on large projects.'
        },
        {
          id: 'contract-pricing',
          question: 'Can I get special pricing for long-term contracts?',
          answer: 'Absolutely! Annual supply agreements include locked-in pricing for 12 months, additional 5-15% discount on contracted items, priority inventory allocation, dedicated account management, and flexible payment terms. Minimum annual commitment: €50,000.'
        },
        {
          id: 'price-matching',
          question: 'Do you offer price matching?',
          answer: 'Yes! We\'ll match or beat any verified competitor quote for identical products and quantities. Provide the competitor\'s written quote, and we\'ll review within 24 hours. Applies to orders over €1,000 from authorized EU distributors. We aim to be your most cost-effective supplier.'
        },
        {
          id: 'loyalty-program',
          question: 'Is there a loyalty program?',
          answer: 'Yes! Our Business Rewards Program offers points on every purchase (1 point per €10 spent), redeemable for discounts, free shipping, or exclusive products. Benefits include early access to new products, exclusive promotions, birthday bonuses, and VIP customer service. Auto-enrollment for all registered customers.'
        }
      ]
    },
    {
      id: 'international',
      title: 'International Shipping',
      icon: 'GlobeAltIcon',
      questions: [
        {
          id: 'countries-served',
          question: 'Which countries do you ship to?',
          answer: 'We ship to all 27 EU member states, UK, Switzerland, Norway, and Iceland. For destinations outside Europe, we arrange international shipping through our global logistics partners. Delivery times and costs vary by destination. Contact our international sales team for non-EU quotes.'
        },
        {
          id: 'customs-duties',
          question: 'Who handles customs and import duties?',
          answer: 'For EU shipments, no customs duties apply. For UK and non-EU destinations, customs duties and VAT are the buyer\'s responsibility unless otherwise agreed. We provide all necessary customs documentation and can arrange DDP (Delivered Duty Paid) shipping for an additional fee.'
        },
        {
          id: 'international-payment',
          question: 'What payment methods for international orders?',
          answer: 'International orders: SWIFT bank transfer, international credit cards (Visa, Mastercard, Amex), PayPal, or Letter of Credit (L/C) for orders over €25,000. Prices quoted in EUR, but we can invoice in GBP, USD, or CHF on request. Currency rates locked at quotation time.'
        },
        {
          id: 'international-support',
          question: 'Do you provide support in multiple languages?',
          answer: 'Yes! Our customer service team provides support in English, French, Dutch, and German. All product documentation, invoices, and correspondence available in your preferred language. Our international sales team is experienced in cross-border trade regulations.'
        }
      ]
    }
  ];

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="max-w-container mx-auto px-6 md:px-12">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 text-accent mb-4">
                <Icon name="QuestionMarkCircleIcon" size={24} />
                <span className="text-sm font-semibold uppercase tracking-wider">Get Answers Fast</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold font-serif mb-6">Frequently Asked Questions</h1>
              <p className="text-lg text-white/90">Everything you need to know about ordering, payments, shipping, and quality standards. Can't find your answer? We're here to help!</p>
            </div>
          </div>
        </section>

        {/* Quick Stats Banner */}
        <section className="bg-accent/10 border-y border-accent/20 py-6">
          <div className="max-w-container mx-auto px-6 md:px-12">
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Icon name="ClockIcon" size={18} className="text-accent" />
                <span className="font-semibold">24-Hour Response Time</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="TruckIcon" size={18} className="text-accent" />
                <span className="font-semibold">Free Shipping €500+</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={18} className="text-accent" />
                <span className="font-semibold">30-Day Returns</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="ReceiptPercentIcon" size={18} className="text-accent" />
                <span className="font-semibold">Volume Discounts Available</span>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="py-16">
          <div className="max-w-container mx-auto px-6 md:px-12">
            <div className="space-y-16">
              {faqCategories.map((category) => (
                <div key={category.id} id={category.id} className="scroll-mt-24">
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-8">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                      <Icon name={category.icon} size={24} className="text-primary" />
                    </div>
                    <h2 className="text-3xl font-bold font-serif text-foreground">{category.title}</h2>
                  </div>

                  {/* Questions Grid */}
                  <div className="grid gap-6">
                    {category.questions.map((faq) => (
                      <div
                        key={faq.id}
                        className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                      >
                        <h3 className="text-lg font-semibold text-foreground mb-3 flex items-start gap-2">
                          <Icon name="QuestionMarkCircleIcon" size={20} className="text-primary mt-0.5 flex-shrink-0" />
                          {faq.question}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed pl-7">{faq.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quick Navigation */}
        <section className="bg-gray-50 py-12">
          <div className="max-w-container mx-auto px-6 md:px-12">
            <h2 className="text-2xl font-bold font-serif text-center mb-8">Jump to Section</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {faqCategories.map((category) => (
                <a
                  key={category.id}
                  href={`#${category.id}`}
                  className="flex items-center gap-2 p-4 bg-white border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all group"
                >
                  <Icon name={category.icon} size={20} className="text-primary" />
                  <span className="text-sm font-medium group-hover:text-primary transition-colors">{category.title}</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16">
          <div className="max-w-container mx-auto px-6 md:px-12 text-center">
            <Icon name="ChatBubbleLeftRightIcon" size={48} className="mx-auto mb-6 text-accent" />
            <h2 className="text-3xl md:text-4xl font-bold font-serif mb-4">Still Have Questions?</h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Our expert team is here to help! Get personalized answers and dedicated support for your business needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-full text-base font-semibold hover:bg-accent/90 transition-colors"
              >
                Contact Our Team
                <Icon name="ArrowUpRightIcon" size={20} />
              </Link>
              <a
                href="tel:+3241234567"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white/20 transition-colors border border-white/20"
              >
                <Icon name="PhoneIcon" size={20} />
                Call +32 4 123 4567
              </a>
            </div>
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <Icon name="ClockIcon" size={16} />
                <span>Mon-Fri: 8AM-6PM CET</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="EnvelopeIcon" size={16} />
                <span>info@suppliesdistributorssa.com</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-12 bg-background">
          <div className="max-w-container mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mx-auto mb-4">
                  <Icon name="ShieldCheckIcon" size={32} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Quality Guaranteed</h3>
                <p className="text-sm text-muted-foreground">ISO certified with 30-day satisfaction guarantee</p>
              </div>
              <div>
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mx-auto mb-4">
                  <Icon name="TruckIcon" size={32} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Fast Delivery</h3>
                <p className="text-sm text-muted-foreground">1-5 business days across EU with tracking</p>
              </div>
              <div>
                <div className="flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mx-auto mb-4">
                  <Icon name="UserGroupIcon" size={32} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Support</h3>
                <p className="text-sm text-muted-foreground">Dedicated team ready to assist in 4 languages</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}